import React, { useMemo } from "react";
import { Stack, Text, Icon } from "@fluentui/react";

interface RawIndicatorRow {
  sectionName?: string;
  metricName?: string;
  factorLabel?: string;
  value: string | number;
}

interface Props { rows: RawIndicatorRow[] }

const toNum = (v: string | number | undefined): number | null => {
  if (v === undefined || v === null || String(v).trim() === "") return null;
  const n = Number(String(v).replace(/[,%]/g, "").trim());
  return Number.isFinite(n) ? n : null;
};

const fmtInt = (n: number | null): string =>
  n === null ? "—" : Math.round(n).toLocaleString();

const fmtPct = (n: number | null): string =>
  n === null ? "—" : `${n.toFixed(2)}%`;

const changeColor = (n: number | null): string =>
  n === null ? "#323130" : n > 0 ? "#107C10" : n < 0 ? "#D13438" : "#605E5C";

interface SubSignalData {
  currentMPU?: number | null;
  prevMPU?: number | null;
  pctChange?: number | null;
  currentDPU?: number | null;
  prevDPU?: number | null;
  currentMPUForUtil?: number | null;
  utilPct?: number | null;
  binText: string;
  scoringBins: string;
  score: number | null;
}

interface TotalData {
  calculation: string;
  totalScore: number | null;
}

interface ParsedChurn {
  mpuStability: SubSignalData;
  dpuStability: SubSignalData;
  dpuUtilization: SubSignalData;
  total: TotalData;
}

function parseSubSignal(rows: RawIndicatorRow[]): SubSignalData {
  const data: SubSignalData = { binText: "", scoringBins: "", score: null };
  for (const row of rows) {
    const lbl = (row.factorLabel ?? "").trim();
    if (/Current Monthly Protected Users \(MPU\)/i.test(lbl)) data.currentMPU = toNum(row.value);
    else if (/3 Months Ago.*Monthly Protected Users \(MPU\)/i.test(lbl)) data.prevMPU = toNum(row.value);
    else if (/Percent Change.*Monthly Protected Users \(MPU\)/i.test(lbl)) data.pctChange = toNum(row.value);
    else if (/Current Deeply Protected Users \(DPU\)/i.test(lbl)) data.currentDPU = toNum(row.value);
    else if (/3 Months Ago.*Deeply Protected Users \(DPU\)/i.test(lbl)) data.prevDPU = toNum(row.value);
    else if (/Percent Change.*Deeply Protected Users \(DPU\)/i.test(lbl)) data.pctChange = toNum(row.value);
    else if (/Current Monthly Protected Users \(MPU\)/i.test(lbl) && data.currentDPU !== undefined) data.currentMPUForUtil = toNum(row.value);
    else if (/Utilization Percent/i.test(lbl)) data.utilPct = toNum(row.value);
    else if (/^Bin:/i.test(lbl)) data.binText = String(row.value ?? "").trim();
    else if (/^Scoring Bins/i.test(lbl)) data.scoringBins = String(row.value ?? "").trim();
    else if (/^Score\s*-/i.test(lbl)) data.score = toNum(row.value);
  }
  return data;
}

function parseUtilRows(rows: RawIndicatorRow[]): SubSignalData {
  const data: SubSignalData = { binText: "", scoringBins: "", score: null };
  for (const row of rows) {
    const lbl = (row.factorLabel ?? "").trim();
    if (/Current Deeply Protected Users \(DPU\)/i.test(lbl)) data.currentDPU = toNum(row.value);
    else if (/Current Monthly Protected Users \(MPU\)/i.test(lbl)) data.currentMPUForUtil = toNum(row.value);
    else if (/Utilization Percent/i.test(lbl)) data.utilPct = toNum(row.value);
    else if (/^Bin:/i.test(lbl)) data.binText = String(row.value ?? "").trim();
    else if (/^Scoring Bins/i.test(lbl)) data.scoringBins = String(row.value ?? "").trim();
    else if (/^Score\s*-/i.test(lbl)) data.score = toNum(row.value);
  }
  return data;
}

function parse(rows: RawIndicatorRow[]): ParsedChurn {
  const grouped: Record<string, RawIndicatorRow[]> = {};
  for (const row of rows) {
    const sec = (row.sectionName ?? "Other").trim();
    if (!grouped[sec]) grouped[sec] = [];
    grouped[sec].push(row);
  }

  let totalCalc = "";
  let totalScore: number | null = null;
  const totalRows = grouped["Total Churn Score"] ?? [];
  for (const row of totalRows) {
    const lbl = (row.factorLabel ?? "").trim();
    if (/Calculation/i.test(lbl)) totalCalc = String(row.value ?? "").trim();
    else if (/Total Churn Score/i.test(lbl)) totalScore = toNum(row.value);
  }

  return {
    mpuStability: parseSubSignal(grouped["MPU Stability (3-month)"] ?? []),
    dpuStability: parseSubSignal(grouped["DPU Stability (3-month)"] ?? []),
    dpuUtilization: parseUtilRows(grouped["DPU Utilization"] ?? []),
    total: { calculation: totalCalc, totalScore },
  };
}

interface SubCardConfig {
  title: string;
  accentColor: string;
  bgColor: string;
  borderColor: string;
  weight: string;
}

const SIGNAL_CONFIGS: SubCardConfig[] = [
  { title: "MPU Stability (3-month)", accentColor: "#1565C0", bgColor: "#E3F2FD", borderColor: "#90CAF9", weight: "33.3%" },
  { title: "DPU Stability (3-month)", accentColor: "#2E7D32", bgColor: "#E8F5E9", borderColor: "#A5D6A7", weight: "33.3%" },
  { title: "DPU Utilization", accentColor: "#E65100", bgColor: "#FFF3E0", borderColor: "#FFCC80", weight: "33.3%" },
];

interface FactorBoxProps { label: string; value: string; valueColor?: string }
const FactorBox: React.FC<FactorBoxProps> = ({ label, value, valueColor = "#323130" }) => (
  <Stack
    horizontalAlign="center"
    tokens={{ childrenGap: 2 }}
    styles={{
      root: {
        backgroundColor: "#FFFFFF",
        border: "1px solid #E0E0E0",
        borderRadius: 6,
        padding: "6px 8px",
        flex: "1 1 0",
        textAlign: "center",
        minWidth: 72,
      },
    }}
  >
    <Text variant="xSmall" styles={{ root: { color: "#757575", lineHeight: "14px" } }}>{label}</Text>
    <Text variant="medium" styles={{ root: { fontWeight: 700, color: valueColor } }}>{value}</Text>
  </Stack>
);

interface SubSignalCardProps {
  config: SubCardConfig;
  data: SubSignalData;
  isMPU?: boolean;
  isDPU?: boolean;
  isUtil?: boolean;
}

const SubSignalCard: React.FC<SubSignalCardProps> = ({ config, data, isMPU, isDPU, isUtil }) => {
  const contribution = data.score !== null ? ((data.score * 1) / 3).toFixed(1) : "—";

  return (
    <Stack
      tokens={{ childrenGap: 10 }}
      styles={{
        root: {
          backgroundColor: config.bgColor,
          border: `1.5px solid ${config.borderColor}`,
          borderRadius: 12,
          padding: "16px 18px",
          flex: "1 1 0",
          minWidth: 200,
        },
      }}
    >
      {/* Header */}
      <Stack horizontal horizontalAlign="space-between" verticalAlign="center" tokens={{ childrenGap: 6 }}>
        <Text variant="small" styles={{ root: { fontWeight: 700, color: config.accentColor } }}>{config.title}</Text>
        <Stack
          styles={{ root: { backgroundColor: config.accentColor, borderRadius: 10, padding: "2px 8px" } }}
        >
          <Text variant="xSmall" styles={{ root: { color: "#FFFFFF", fontWeight: 700 } }}>
            {data.score !== null ? data.score : "—"}
          </Text>
        </Stack>
      </Stack>

      {/* Score / Weight / Contribution */}
      <Stack horizontal tokens={{ childrenGap: 12 }}>
        <Stack tokens={{ childrenGap: 0 }}>
          <Text variant="xSmall" styles={{ root: { color: "#605E5C" } }}>Score</Text>
          <Text variant="medium" styles={{ root: { fontWeight: 700, color: config.accentColor } }}>
            {data.score !== null ? data.score : "—"}
          </Text>
        </Stack>
        <Stack tokens={{ childrenGap: 0 }}>
          <Text variant="xSmall" styles={{ root: { color: "#605E5C" } }}>Weight</Text>
          <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>{config.weight}</Text>
        </Stack>
        <Stack tokens={{ childrenGap: 0 }}>
          <Text variant="xSmall" styles={{ root: { color: "#605E5C" } }}>Contribution</Text>
          <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>{contribution}</Text>
        </Stack>
      </Stack>

      {/* Factor boxes */}
      <Stack horizontal tokens={{ childrenGap: 6 }}>
        {isMPU && (
          <>
            <FactorBox label="Current MPU" value={fmtInt(data.currentMPU ?? null)} />
            <FactorBox label="3-mo Ago MPU" value={fmtInt(data.prevMPU ?? null)} />
            <FactorBox label="MPU Change" value={fmtPct(data.pctChange ?? null)} valueColor={changeColor(data.pctChange ?? null)} />
          </>
        )}
        {isDPU && (
          <>
            <FactorBox label="Current DPU" value={fmtInt(data.currentDPU ?? null)} />
            <FactorBox label="3-mo Ago DPU" value={fmtInt(data.prevDPU ?? null)} />
            <FactorBox label="DPU Change" value={fmtPct(data.pctChange ?? null)} valueColor={changeColor(data.pctChange ?? null)} />
          </>
        )}
        {isUtil && (
          <>
            <FactorBox label="Current DPU" value={fmtInt(data.currentDPU ?? null)} />
            <FactorBox label="Current MPU" value={fmtInt(data.currentMPUForUtil ?? null)} />
            <FactorBox label="Util. Rate" value={fmtPct(data.utilPct ?? null)} valueColor={data.utilPct != null && data.utilPct >= 50 ? "#107C10" : "#D13438"} />
          </>
        )}
      </Stack>

      {/* Bin text */}
      {data.binText && (
        <Text variant="xSmall" styles={{ root: { color: config.accentColor, fontWeight: 600, lineHeight: "16px" } }}>
          Bin: {data.binText}
        </Text>
      )}

      {/* Scoring bins */}
      {data.scoringBins && (
        <>
          <div style={{ height: 1, backgroundColor: config.borderColor }} />
          <Stack tokens={{ childrenGap: 2 }}>
            <Text variant="xSmall" styles={{ root: { fontWeight: 700, color: "#605E5C" } }}>Scoring Bins:</Text>
            <Text variant="xSmall" styles={{ root: { color: "#323130", lineHeight: "15px", whiteSpace: "pre-line" } }}>{data.scoringBins}</Text>
          </Stack>
        </>
      )}
    </Stack>
  );
};

const PurviewChurnRawIndicatorCards: React.FC<Props> = ({ rows }) => {
  const data = useMemo(() => parse(rows), [rows]);
  const totalScoreDisplay = data.total.totalScore !== null ? data.total.totalScore : "—";
  const totalColor =
    data.total.totalScore === null ? "#323130"
      : data.total.totalScore >= 71 ? "#107C10"
        : data.total.totalScore >= 41 ? "#C15A00"
          : "#D13438";

  return (
    <Stack tokens={{ childrenGap: 16 }}>
      {/* Sub-signal cards */}
      <Stack horizontal tokens={{ childrenGap: 12 }} wrap styles={{ root: { alignItems: "stretch" } }}>
        <SubSignalCard config={SIGNAL_CONFIGS[0]} data={data.mpuStability} isMPU />
        <SubSignalCard config={SIGNAL_CONFIGS[1]} data={data.dpuStability} isDPU />
        <SubSignalCard config={SIGNAL_CONFIGS[2]} data={data.dpuUtilization} isUtil />
      </Stack>

      {/* Total Churn Score */}
      <Stack
        horizontal
        verticalAlign="center"
        tokens={{ childrenGap: 20 }}
        styles={{
          root: {
            backgroundColor: "#FAF5FF",
            border: "1.5px solid #CE93D8",
            borderRadius: 10,
            padding: "14px 20px",
          },
        }}
      >
        <Stack tokens={{ childrenGap: 2 }}>
          <Text variant="small" styles={{ root: { fontWeight: 700, color: "#742774" } }}>Total Churn Score</Text>
          {data.total.calculation && (
            <Text variant="xSmall" styles={{ root: { color: "#605E5C" } }}>{data.total.calculation}</Text>
          )}
        </Stack>
        <Stack
          horizontalAlign="center"
          verticalAlign="center"
          styles={{
            root: {
              backgroundColor: totalColor,
              borderRadius: 20,
              padding: "4px 16px",
              minWidth: 52,
            },
          }}
        >
          <Text variant="large" styles={{ root: { fontWeight: 700, color: "#FFFFFF" } }}>
            {totalScoreDisplay}
          </Text>
        </Stack>
        <Icon iconName="CheckMark" styles={{ root: { fontSize: 18, color: totalColor } }} />
      </Stack>
    </Stack>
  );
};

export default PurviewChurnRawIndicatorCards;
